import {
  AnalyticsActionProductSelectedEvent,
  AnalyticsEventParam,
  CustomParameters,
  PurchasableProduct,
  ScreenType,
} from '@accedo/one-plugin-sdk';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { PackageDefinition } from '#/client/models';
import { PickerItemBaseProps } from '#/components/Picker/PickerItem';
import { ProductCard } from '#/components/ProductPicker/ProductCard';
import {
  DetailSubtext,
  DetailText,
} from '#/components/ProductPicker/ProductCard.styled';
import { ProductTab } from '#/components/ProductPicker/ProductTabs';
import { useConfig } from '#/hooks/useConfig';
import { useNavigation } from '#/hooks/useNavigation';
import {
  durationToIso8601,
  getScreenTitle,
} from '#/plugins/analytics/analyticsUtils';
import { FormattedPrice } from '#/plugins/paywall/FormattedPrice';
import { ignoreError } from '#/service/utils';
import { usePaywallStore } from '#/store/paywall/paywall.store';
import { tb } from '#/store/translation/translation.store';

import { SubscriptionParams } from '../PlanPurchaseFlow.types';
import { getPickerItems } from './getPickerItems';
import { getDisclaimer } from './PickProductStep.utils';

interface UsePickProductStepProps {
  onProductPicked: (product: PurchasableProduct) => void;
  customParameters?: CustomParameters;
  purchasableProducts: PurchasableProduct[];
  packageDefinition: PackageDefinition;
}

export const usePickProductStep = ({
  customParameters,
  onProductPicked,
  purchasableProducts,
  packageDefinition,
}: UsePickProductStepProps) => {
  const { paywallApi } = usePaywallStore();
  const { featureConfig } = useConfig();
  const disclaimer = getDisclaimer();

  const { planPickerScreen } = featureConfig.purchase?.inApp ?? {};
  const { showSkipPurchaseButton } = planPickerScreen ?? {};

  const [renderContent, setRenderContent] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<PurchasableProduct>();
  const { goBack } = useNavigation('PickProductStep');

  // convert purchasable products to sorted picker items, including headings
  const pickerItems = useMemo(() => {
    return getPickerItems(purchasableProducts, packageDefinition);
  }, [purchasableProducts, packageDefinition]);

  // Extract available tabs from pickerItems with translated labels
  const availableTabs = useMemo((): ProductTab[] => {
    const tabIds = pickerItems.map((item) => item.tabId).filter(Boolean);

    const uniqueTabIds = [...new Set(tabIds)];

    return uniqueTabIds.map((tabId) => ({
      id: tabId,
      label: tb(`iap.productType.${tabId.toLowerCase()}`),
    }));
  }, [pickerItems]);

  // Custom renderItem with price split logic
  const renderItem = useCallback(
    (
      item: PickerItemBaseProps & { id: string },
      onContinue: (id: string) => void,
      autoFocus: boolean | undefined
    ): ReactElement => {
      return (
        <ProductCard
          autoFocus={autoFocus}
          item={item}
          onContinue={onContinue}
          renderDetail={(detail) => (
            <FormattedPrice
              AmountComponent={DetailText}
              DurationComponent={DetailSubtext}
              translatedPrice={detail}
            />
          )}
        />
      );
    },
    []
  );

  const { onCancel, cancelButtonTranslationKey } = useMemo(() => {
    if (showSkipPurchaseButton) {
      return {
        onCancel: goBack,
        cancelButtonTranslationKey: 'iap.planSelection.button.skip',
      };
    }

    return {};
  }, [goBack, showSkipPurchaseButton]);

  const handleOnContinue = useCallback(
    async (productId: string) => {
      const product = purchasableProducts.find(({ id }) => id === productId);

      if (!product) {
        return;
      }

      const { vendorTag } = customParameters ?? {};
      // we need to make a copy, so we can extend customParameters despite valtio.
      setSelectedProduct({
        ...product,
        customParameters: {
          ...product.customParameters,
          ...(vendorTag && { vendorTag }),
        },
      });

      paywallApi
        .getProviderInfo()
        .then(({ name }) => {
          document.dispatchEvent(
            new AnalyticsActionProductSelectedEvent({
              [AnalyticsEventParam.ScreenType]: ScreenType.ProductPicker,
              [AnalyticsEventParam.ScreenTitle]: getScreenTitle(),
              [AnalyticsEventParam.ScreenHeading]: tb('iap.selectYourPlan'),
              [AnalyticsEventParam.ProductId]: product.id,
              [AnalyticsEventParam.Price]: Number(product.price),
              [AnalyticsEventParam.Currency]: product.currency,
              [AnalyticsEventParam.SubscriptionPlan]: product.title,
              [AnalyticsEventParam.SubscriptionPeriod]: durationToIso8601(
                product?.duration
              ),
              [AnalyticsEventParam.Store]: name,
            })
          );
        })
        .catch(ignoreError);

      onProductPicked(product);
    },
    [onProductPicked, purchasableProducts, customParameters, paywallApi]
  );

  useEffect(() => {
    if (!selectedProduct) {
      const searchParams = new URLSearchParams(window.location.search);
      const paramsProductId = searchParams.get(SubscriptionParams.Product);

      const productId = purchasableProducts.find(
        ({ id }) => id === paramsProductId
      )?.id;

      if (productId) {
        handleOnContinue(productId);
      }
    }
    setRenderContent(true);
  }, [handleOnContinue, purchasableProducts, selectedProduct]);

  return {
    disclaimer,
    renderContent,
    pickerItems,
    selectedProduct,
    purchasableProducts,
    handleOnContinue,
    onCancel,
    cancelButtonTranslationKey,
    availableTabs,
    renderItem,
  };
};
